// Pure helpers for adding recipe ingredients to an ingredient list. The
// home page's useIngredients hook does the same dedup/categorize/queue
// dance for typed lines; this module extracts the dedup+categorize half
// so the recipe sheet can reuse it without owning home's hook state.
//
// Limits checks live on the caller — those depend on isAuthenticated /
// isPremium, which isn't relevant to the pure transformation here.
import { dedupeIngredientsWithActions } from "@/lib/categorization/dedupe";
import {
  categorizeIngredient,
  parseQuantity,
  processIngredientText,
} from "@/lib/categorization/utils";
import type { Category, Ingredient } from "@/lib/types";
import { generateId } from "@/lib/utils";

export interface ExistingItem {
  id: string;
  text: string;
  quantity: string | undefined;
  category: Category;
}

export interface AdditionUpdate {
  id: string;
  newQuantity: string | undefined;
  category: Category;
}

export interface IngredientAdditionPlan {
  newIngredients: Ingredient[];
  updatesToExisting: AdditionUpdate[];
}

/**
 * Categorize input lines, dedup against existing list items, and return
 * the structured plan: which existing rows to bump (quantity merge), and
 * which new ingredient rows to create.
 *
 * Mirrors the logic at the top of useIngredients.addIngredients so the
 * recipe → list sheet behaves the same way as typing items into a list.
 */
export function buildIngredientAdditions(
  lines: string[],
  existingItems: ExistingItem[]
): IngredientAdditionPlan {
  const trimmed = lines.map((s) => s.trim()).filter((s) => s.length > 0);
  if (trimmed.length === 0) return { newIngredients: [], updatesToExisting: [] };

  const { updates, additions } = dedupeIngredientsWithActions(
    trimmed,
    existingItems.map((i) => ({ id: i.id, text: i.text, quantity: i.quantity }))
  );

  const updatesToExisting: AdditionUpdate[] = updates.map((u) => {
    const existing = existingItems.find((i) => i.id === u.id);
    return {
      id: u.id,
      newQuantity: u.newQuantity,
      category: existing?.category ?? ("other" as Category),
    };
  });

  const newIngredients: Ingredient[] = additions.map((text) => {
    const quantity = parseQuantity(text);
    const category = categorizeIngredient(text);
    const processedText = processIngredientText(text, quantity);
    return {
      id: generateId(),
      text: processedText,
      category,
      quantity,
      completed: false,
    };
  });

  return { newIngredients, updatesToExisting };
}
